import type { AIStore } from '..';
import type { Tool, StepInfo } from '@readme/api/src/mappings/chat/types';
import type { StateCreator } from 'zustand';

import type useLocalStorage from '@core/hooks/useLocalStorage';
import { actionLog } from '@core/store/util';

import { cacheKey, failSilently, reviveDates } from './storage';

export interface Message {
  content: string;
  id: string;
  isLoading?: boolean;
  pending_tool_confirmation?: Tool;
  role: 'assistant' | 'user';
  stepUpdates?: StepInfo[];
  timestamp: Date;
  tools?: Tool[];
}

export interface ChatModel {
  enabled: boolean;
  id: string;
  name: string;
  provider: string;
}

interface Checkpoint {
  action: string;
  content: Record<string, unknown> | string;
  timestamp: Date;
}

interface AgentContext {
  /**
   * Hidden context is not shown to the user, but included in the prompt
   */
  hiddenContext?: Record<string, string>;
  message?: string;
  prompt?: string;
  runImmediately?: boolean;
  title?: string;
  type?: string;
}

interface AiAgentSliceState {
  chatId: string | null;
  checkpoints: Checkpoint[];
  /**
   * Context for pre-populating chat input when navigating from other components.
   */
  context?: AgentContext;
  messages: Message[];
  model: ChatModel;
  storage?: ReturnType<typeof useLocalStorage>;
}

type Setter<T> = (value: T | ((prev: T) => T)) => void;

interface AiAgentSliceAction {
  addCheckpoint: (checkpoint: { content: Record<string, unknown> | string; action: string }) => number;
  /**
   * Clears context after it has been consumed.
   */
  clearContext: () => void;
  initialize: (params: { chatId: string; storage: ReturnType<typeof useLocalStorage> }) => void;
  popCheckpoint: () => void;
  reset: () => void;
  resetMessages: () => void;
  /**
   * Sets AI context for pre-populating chat input.
   */
  setContext: (context: AgentContext) => void;
  setMessages: Setter<Message[]>;
  setModel: (model: ChatModel) => void;
  updateChatId: (chatId: string, messageCount: number) => void;
}

export interface AiAgentSlice {
  agent: AiAgentSliceAction & AiAgentSliceState;
}

export const defaultModel = { id: 'gemini-2.5-flash', provider: 'google', name: 'Gemini 2.5 Flash', enabled: true };

const initialState: AiAgentSliceState = {
  chatId: null,
  checkpoints: [],
  context: {},
  messages: [],
  model: defaultModel,
  storage: undefined,
};

export const createAiAgentSlice: StateCreator<
  AIStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  AiAgentSlice
> = (set, get) => ({
  agent: {
    ...initialState,

    addCheckpoint: checkpoint => {
      const newCheckpoint: Checkpoint = {
        ...checkpoint,
        timestamp: new Date(),
      };

      set(
        state => {
          state.agent.checkpoints.push(newCheckpoint);
        },
        false,
        actionLog('ai.agent.addCheckpoint', checkpoint),
      );

      return get().agent.checkpoints.length;
    },

    clearContext: () => {
      set(
        state => {
          state.agent.context = {};
        },
        false,
        actionLog('ai.agent.clearContext'),
      );
    },

    initialize: ({ chatId, storage }) => {
      const model = storage.getItem('model') as ChatModel | null;
      const messages = reviveDates((storage.getItem(cacheKey(chatId)) as Message[]) || []) as Message[];

      set(
        state => {
          Object.assign(state.agent, {
            ...initialState,
            chatId,
            storage,
            messages,
          });

          if (model) {
            state.agent.model = model;
          }
        },
        false,
        actionLog('ai.agent.initialize', { chatId }),
      );
    },

    popCheckpoint: () => {
      set(
        state => {
          state.agent.checkpoints.pop();
        },
        false,
        actionLog('ai.agent.popCheckpoint'),
      );
    },

    reset: () => {
      set(
        state => {
          Object.assign(state.agent, initialState);
        },
        false,
        actionLog('ai.agent.reset'),
      );
    },

    resetMessages: () => {
      set(
        state => {
          state.agent.messages = [];
        },
        false,
        actionLog('ai.agent.resetMessages'),
      );

      const { chatId, storage } = get().agent;
      if (!chatId || !storage) return;

      failSilently(() => storage.removeItem(cacheKey(chatId)));
    },

    setContext: context => {
      set(
        state => {
          state.agent.context = context;
        },
        false,
        actionLog('ai.agent.setContext', { context }),
      );
    },

    setMessages: messagesOrFunc => {
      const messages = typeof messagesOrFunc === 'function' ? messagesOrFunc(get().agent.messages) : messagesOrFunc;

      set(
        state => {
          state.agent.messages = messages;
        },
        false,
        actionLog('ai.agent.setMessages', { messages: messagesOrFunc }),
      );

      const { chatId, storage } = get().agent;
      if (!chatId || !storage) return;

      failSilently(() => storage.setItem(cacheKey(chatId), messages));
    },

    setModel: model => {
      set(
        state => {
          state.agent.model = model;
        },
        false,
        actionLog('ai.agent.setModel', { model }),
      );
      const { storage } = get().agent;
      if (!storage) return;

      failSilently(() => storage.setItem('model', model));
    },

    updateChatId: (chatId: string, messageCount: number) => {
      set(
        state => {
          state.agent.chatId = chatId;
        },
        false,
        actionLog('ai.agent.updateChatId', { chatId, messageCount }),
      );
    },
  },
});
